/**
 * ConnectingHint — «Соединяемся…» beside the typing dots (owner-approved
 * 2026-10-13).
 *
 * The owner answered a clarify round and watched the bare dots for ~15 s: the
 * request was stuck on the network, not in the app. The dots were right, but
 * they cannot say that a wait is unusual. After 4 s with NO frame for the turn
 * this adds a quiet label beside them; the dots keep running.
 *
 * Honest by construction: `since` is the store's `awaitingFirstFrameSince`,
 * set by `beginStream` and cleared for good by the turn's first frame of any
 * type, by Stop, an error, a thread switch and «Новый чат». So the hint can
 * only ever show while nothing at all has arrived for this turn. It is not a
 * stage — the stage vocabulary (`chat-stage.ts`) is untouched, and later
 * waits belong to the StageIndicator. The clock counts from the turn's start,
 * not from this component's mount, so a remount never restarts it.
 *
 * Accessibility: the dots are already a status. This polite live region
 * mounts EMPTY with the clock and the text is inserted once, so a screen
 * reader announces it once when it appears; re-renders leave the node alone.
 *
 * Motion: a short fade and rise on the house tokens (`@/lib/motion`); under
 * reduced motion a plain fade.
 *
 * DESIGN-SYSTEM CALLOUT (GUARDRAILS rule 10): the design draws no slow-
 * connection state. Drafted inside the system: the StageIndicator lead
 * label's type (14px medium) and ink (`--g-ink-2`, the `.g-shimmer` base),
 * WITHOUT the shimmer, which means named work, and the dots already carry the
 * liveness. Flagged for design review rather than presented as canon.
 */
import { motion, useReducedMotion, type Transition } from "framer-motion";
import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { MOTION } from "@/lib/motion";

/** How long a turn may go without any frame before the dots say so. */
export const CONNECTING_HINT_DELAY_MS = 4000;

const ENTER: Transition = { duration: MOTION.dur.bubble, ease: MOTION.ease.out };

export interface ConnectingHintProps {
  /** When the live turn began waiting for its first frame (epoch ms); null
   * once any frame arrived, or when no turn is waiting. */
  since: number | null;
}

export function ConnectingHint({ since }: ConnectingHintProps): ReactElement | null {
  const { t } = useTranslation("chat");
  const reduced = useReducedMotion() ?? false;
  // The `since` whose 4 s have run out. Keyed by the turn's start, so a new
  // turn's clock never inherits the previous turn's "shown".
  const [elapsedFor, setElapsedFor] = useState<number | null>(null);

  useEffect(() => {
    if (since === null) return undefined;
    const wait = Math.max(0, CONNECTING_HINT_DELAY_MS - (Date.now() - since));
    const timer = setTimeout(() => setElapsedFor(since), wait);
    return () => clearTimeout(timer);
  }, [since]);

  if (since === null) return null;

  return (
    <span role="status" aria-live="polite" data-testid="chat-connecting">
      {elapsedFor === since ? (
        <motion.span
          className="ms-2.5 text-[15px] font-medium text-[var(--g-ink-2)]"
          data-testid="chat-connecting-hint"
          data-motion={reduced ? "fade" : "rise"}
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 4 }}
          animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
          transition={ENTER}
        >
          {t("stream.connecting")}
        </motion.span>
      ) : null}
    </span>
  );
}

export default ConnectingHint;
